import {Auth} from 'aws-amplify';
import {createSlice} from '@reduxjs/toolkit';

function startLoading(state) {
  state.isLoading = true;
}

function loadingFailed(state, action) {
  state.isLoading = false;
  state.error = action.payload;
}

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isLoading: false,
    user: null,
  },
  reducers: {
    fetchUserStart: startLoading,
    fetchUserSuccess: (state, action) => {
      state.user = action.payload;
      state.error = null;
      state.isLoading = false;
    },
    fetchUserFailure: loadingFailed,
    registerStart: startLoading,
    registerSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    registerFailure: loadingFailed,
    logInStart: startLoading,
    logInSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    logInFailure: loadingFailed,
    logOutStart: startLoading,
    logOutSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    logOutFailure: loadingFailed,
    resendEmailConfirmationCodeStart: startLoading,
    resendEmailConfirmationCodeSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    resendEmailConfirmationCodeFailure: loadingFailed,
    confirmUserStart: startLoading,
    confirmUserSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    confirmUserFailed: loadingFailed,
    forgotPasswordStart: startLoading,
    forgotPasswordSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    forgotPasswordFailed: loadingFailed,
    forgotPasswordSubmitStart: startLoading,
    forgotPasswordSubmitSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    forgotPasswordSubmitFailed: loadingFailed,
    changePasswordStart: startLoading,
    changePasswordSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    changePasswordFailed: loadingFailed,
    // updateProfileStart: startLoading,
    // updateProfileSuccess: (state) => {
    //   state.isLoading = false;
    //   state.error = null;
    // },
    // updateProfileFailed: loadingFailed,
  },
});

export const {
  fetchUserStart,
  fetchUserSuccess,
  fetchUserFailure,
  registerStart,
  registerSuccess,
  registerFailure,
  logInStart,
  logInSuccess,
  logInFailure,
  logOutStart,
  logOutSuccess,
  logOutFailure,
  resendEmailConfirmationCodeStart,
  resendEmailConfirmationCodeSuccess,
  resendEmailConfirmationCodeFailure,
  confirmUserStart,
  confirmUserSuccess,
  confirmUserFailed,
  forgotPasswordStart,
  forgotPasswordSuccess,
  forgotPasswordFailed,
  forgotPasswordSubmitStart,
  forgotPasswordSubmitSuccess,
  forgotPasswordSubmitFailed,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailed,
  // updateProfileStart,
  // updateProfileSuccess,
  // updateProfileFailed,
} = authSlice.actions;

export const fetchUser = () => async (dispatch) => {
  try {
    dispatch(fetchUserStart());

    const user = await Auth.currentAuthenticatedUser();

    dispatch(fetchUserSuccess(user.attributes));
  } catch (error) {
    dispatch(fetchUserFailure(error.toString()));
    throw error;
  }
};

export const logIn = (username, password) => async (dispatch) => {
  try {
    dispatch(logInStart());

    // Cognito allows case sensitive email addresses
    const usernameLowercase = username.toLowerCase();
    const signInResponse = await Auth.signIn(usernameLowercase, password);

    dispatch(logInSuccess());

    return signInResponse;
  } catch (error) {
    dispatch(logInFailure(error.toString()));
    throw error;
  }
};

export const logOut = () => async (dispatch) => {
  try {
    dispatch(logOutStart());

    const signOutResponse = await Auth.signOut();

    dispatch(logOutSuccess());

    return signOutResponse;
  } catch (error) {
    dispatch(logOutFailure(error.toString()));
    throw error;
  }
};

export const register = (firstName, lastName, username, password, inviteCode) => async (dispatch) => {
  try {
    dispatch(registerStart());

    const params = {
      username: username.toLowerCase(), // Cognito allows case sensitive email addresses
      password,
      attributes: {
        given_name: firstName,
        family_name: lastName,
        'custom:inviteCode': inviteCode,
      },
    };
    const signUpResponse = await Auth.signUp(params);

    dispatch(registerSuccess());

    return signUpResponse;
  } catch (error) {
    dispatch(registerFailure(error.toString()));
    throw error;
  }
};

export const resendEmailConfirmationCode = (username) => async (dispatch) => {
  try {
    dispatch(resendEmailConfirmationCodeStart());

    // Cognito allows case sensitive email addresses
    const usernameLowercase = username.toLowerCase();
    const resendSignUpResponse = await Auth.resendSignUp(usernameLowercase);

    dispatch(resendEmailConfirmationCodeSuccess());

    return resendSignUpResponse;
  } catch (error) {
    dispatch(resendEmailConfirmationCodeFailure(error.toString()));
    throw error;
  }
};

export const confirmUser = (username, code) => async (dispatch) => {
  try {
    dispatch(confirmUserStart());

    // Cognito allows case sensitive email addresses
    const usernameLowercase = username.toLowerCase();
    const confirmSignUpResponse = await Auth.confirmSignUp(usernameLowercase, code);

    dispatch(confirmUserSuccess());

    return confirmSignUpResponse;
  } catch (error) {
    dispatch(confirmUserFailed(error.toString()));
    throw error;
  }
};

export const forgotPassword = (username) => async (dispatch) => {
  try {
    dispatch(forgotPasswordStart());

    // Cognito allows case sensitive email addresses
    const usernameLowercase = username.toLowerCase();
    const forgotPasswordResponse = await Auth.forgotPassword(usernameLowercase);

    dispatch(forgotPasswordSuccess());

    return forgotPasswordResponse;
  } catch (error) {
    dispatch(forgotPasswordFailed(error.toString()));
    throw error;
  }
};

export const forgotPasswordSubmit = (username, code, password) => async (dispatch) => {
  try {
    dispatch(forgotPasswordSubmitStart());

    // Cognito allows case sensitive email addresses
    const usernameLowercase = username.toLowerCase();
    const forgotPasswordResponse = await Auth.forgotPasswordSubmit(usernameLowercase, code, password);

    dispatch(forgotPasswordSubmitSuccess());

    return forgotPasswordResponse;
  } catch (error) {
    dispatch(forgotPasswordSubmitFailed(error.toString()));
    throw error;
  }
};

export const changePassword = (currentPassword, newPassword) => async (dispatch) => {
  try {
    dispatch(changePasswordStart());

    const user = await Auth.currentAuthenticatedUser();
    const changePasswordResponse = await Auth.changePassword(user, currentPassword, newPassword);

    dispatch(changePasswordSuccess());

    return changePasswordResponse;
  } catch (error) {
    dispatch(changePasswordFailed(error.toString()));
    throw error;
  }
};

// export const updateProfile = ({firstName, lastName, picture}) => async (dispatch) => {
//   try {
//     dispatch(updateProfileStart());

//     const user = await Auth.currentAuthenticatedUser();
//     const updateUserResponse = await Auth.updateUserAttributes(user, {
//       given_name: firstName,
//       family_name: lastName,
//       picture,
//     });

//     dispatch(updateProfileSuccess());
//     await dispatch(fetchUser());

//     return updateUserResponse;
//   } catch (error) {
//     dispatch(updateProfileFailed(error.toString()));
//     throw error;
//   }
// };

export const selectIsLoading = (state) => state.auth.isLoading;
export const selectUserAttributes = (state) => state.auth.user;
export const selectUserID = (state) => state.auth?.user?.sub;

export default authSlice.reducer;
